<script setup>
// Potwierdzenie wpisu z dyktowania (POST /ai/entries) — montowane globalnie w app.vue.
//  • trening, posiłek: API tylko wyciąga dane → formularz dodawania wypełniony z góry;
//  • sen: API już zapisało wpis → formularz edycji tego wpisu do przejrzenia;
//  • zadanie, wydarzenie, wpis dnia: „Edytuj" w podsumowaniu dyktowania → formularz edycji.
// Wynik (zapisano / usunięto / zamknięto) wraca do podsumowania przez `settle`.
// Słowniki (rodzaje treningów, posiłki, produkty) dociągamy dopiero przy otwarciu.
import { computed, ref, watch } from 'vue';
import AddDialog from '@/components/v2/add-dialog.vue';
import { useApi } from '~/composables/useApi.ts';
import { useAiEntries } from '@/composables/useAiEntries.js';

const api = useApi();
const {
    workoutDialogOpen, workoutDraft, closeWorkoutDraft,
    sleepDialogOpen, sleepEditId, closeSleepReview,
    mealDialogOpen, mealDraft, closeMealDraft,
    editTarget, closeEntryEdit, settle,
    notifyCreated,
} = useAiEntries();

const pad = (n) => String(n).padStart(2, '0');
const startOfDay = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
const hm = (d) => `${pad(d.getHours())}:${pad(d.getMinutes())}`;
const str = (v) => (v == null ? '' : String(v));

const workoutTypes = ref([]);
const meals = ref([]);
const products = ref([]);
const sleepEntry = ref(null);

// Co jest teraz otwarte: { type, prefill?, entry? } albo null.
const open = computed(() => {
    if (workoutDialogOpen.value) {
        const d = workoutDraft.value ?? {};
        const at = d.start_time ? new Date(d.start_time) : new Date();
        const seconds = d.duration_seconds ?? null;
        return {
            type: 'workout',
            day: startOfDay(at),
            prefill: {
                day: startOfDay(at),
                time: hm(at),
                workoutType: d.workout_type?.uuid ?? null,
                workoutStatus: 'done',
                durationH: seconds == null ? '' : str(Math.floor(seconds / 3600)),
                durationMin: seconds == null ? '' : str(Math.round((seconds % 3600) / 60)),
                activeCalories: '',
                distanceKm: d.distance_meters == null ? '' : String(d.distance_meters / 1000),
            },
        };
    }
    if (mealDialogOpen.value) {
        const d = mealDraft.value ?? {};
        const at = d.planned_at ? new Date(d.planned_at) : new Date();
        return {
            type: 'meal',
            day: startOfDay(at),
            prefill: {
                day: startOfDay(at),
                time: hm(at),
                name: d.name ?? '',
                status: d.status === 'eaten' ? 'eaten' : 'planned',
                calories: str(d.calories),
                protein: str(d.protein),
                carbs: str(d.carbs),
                fat: str(d.fat),
                saturated_fat: str(d.saturated_fat),
                sugar: str(d.sugar),
                fiber: str(d.fiber),
                salt: str(d.salt),
            },
        };
    }
    if (sleepDialogOpen.value && sleepEntry.value) {
        return { type: 'sleep', day: startOfDay(sleepEntry.value.date ?? new Date()), entry: sleepEntry.value };
    }
    if (editTarget.value) {
        const { type, entry } = editTarget.value;
        const at = entry.planned_for ?? entry.starts_at ?? entry.start_date ?? entry.reported_at ?? entry.due_at ?? new Date();
        return { type, day: startOfDay(at), entry };
    }
    return null;
});

watch(workoutDialogOpen, async (v) => {
    if (!v || workoutTypes.value.length) return;
    try { workoutTypes.value = await api.get('/workout-types'); } catch { workoutTypes.value = []; }
});
watch(mealDialogOpen, async (v) => {
    if (!v) return;
    try {
        const [m, p] = await Promise.all([api.get('/meals'), api.get('/products')]);
        meals.value = m;
        products.value = p;
    } catch { /* formularz działa i bez katalogu */ }
});
watch(sleepDialogOpen, async (v) => {
    sleepEntry.value = null;
    if (!v || !sleepEditId.value) return;
    try { sleepEntry.value = await api.get('/sleep-entries/' + sleepEditId.value); } catch { closeSleepReview(); }
});

const close = () => {
    if (workoutDialogOpen.value) closeWorkoutDraft();
    if (mealDialogOpen.value) closeMealDraft();
    if (sleepDialogOpen.value) closeSleepReview();
    if (editTarget.value) closeEntryEdit();
};
const saved = () => { notifyCreated(); settle('saved'); close(); };
const deleted = () => { notifyCreated(); settle('deleted'); close(); };
</script>

<template>
    <AddDialog
        :show="!!open"
        :day="open?.day ?? new Date()"
        :initial-type="open?.type ?? 'note'"
        :entry="open?.entry ?? null"
        :prefill="open?.prefill ?? null"
        :workout-types="workoutTypes"
        :meals="meals"
        :products="products"
        @close="close"
        :tasks-available="true"
        @saved="saved"
        @deleted="deleted"
    />
</template>
